/**
 * SONAUTO — locale catalogue.
 *
 * Vietnamese (vi-VN) is the SOURCE language of the whole product. Everything a
 * STAFF member sees (Admin App, POS App, Staff App) stays Vietnamese; only the
 * CUSTOMER surface (QR ordering, order status, receipts shown to the guest) is
 * multilingual.
 *
 * ---------------------------------------------------------------------------
 * EXTENSIBILITY CONTRACT (non-negotiable)
 * ---------------------------------------------------------------------------
 * Enabling a new language must require EXACTLY two things:
 *
 *   1. one new entry in `SUPPORTED_LOCALES` below (uncomment or add), and
 *   2. the matching rows in the database
 *      (`supported_locales`, `tenant_locales` / `branch_locales`, and the
 *      `customer_ui_translations` rows for that locale).
 *
 * It must NEVER require touching negotiation, formatting, bundle or pipeline
 * code. Nothing in this package may branch on a specific locale string; per
 * locale behaviour has to be data on the entry itself. Five ready-to-enable
 * entries are kept commented out at the bottom of the array as living proof
 * that the rule holds.
 *
 * The shape mirrors `supported_locales` in
 * database/migrations/0005_translations.sql column for column, so a row and an
 * entry are trivially diffable (`is_enabled_globally` is deliberately NOT
 * mirrored — enablement is runtime data, not a build time constant).
 */

/** Writing direction. Mirrors `supported_locales.direction`. */
export type TextDirection = 'LTR' | 'RTL';

/**
 * A BCP-47 locale tag such as `vi-VN`.
 *
 * Deliberately `string` and not a closed union: locales enabled for a tenant
 * come from D1 at runtime, and a tag that is not (yet) in this catalogue must
 * degrade gracefully rather than fail to compile. Use `isSupportedLocale()`
 * when you need the narrow type.
 */
export type Locale = string;

export interface LocaleInfo {
  /** BCP-47 tag, canonical casing: lowercase language, uppercase region. */
  readonly locale: string;
  /** ISO 639-1, e.g. `vi`, `zh`, `ru`, `en`. */
  readonly languageCode: string;
  /** ISO 3166-1 alpha-2, e.g. `VN`. `null` for language-only tags. */
  readonly regionCode: string | null;
  /** Endonym — what the guest sees in the language switcher, e.g. `中文`. */
  readonly nameNative: string;
  /** Vietnamese label, used by the Admin App locale settings screen. */
  readonly nameVi: string;
  /** English label, used in exports and support tooling. */
  readonly nameEn: string;
  readonly direction: TextDirection;
  /** Display pattern hint for humans/admin UI. Formatting itself uses Intl. */
  readonly dateFormat: string;
  /** Sample of the grouping/decimal convention, e.g. `1.234,56`. */
  readonly numberFormat: string;
  /** Display order in the language switcher. Lower first. */
  readonly sortOrder: number;
}

/**
 * The catalogue. ADD A LOCALE HERE — nowhere else.
 *
 * `satisfies` (rather than a type annotation) keeps the literal types so
 * `KnownLocale` stays exact, while `as const` keeps the array readonly.
 */
export const SUPPORTED_LOCALES = [
  {
    locale: 'vi-VN',
    languageCode: 'vi',
    regionCode: 'VN',
    nameNative: 'Tiếng Việt',
    nameVi: 'Tiếng Việt',
    nameEn: 'Vietnamese',
    direction: 'LTR',
    dateFormat: 'dd/MM/yyyy',
    numberFormat: '1.234,56',
    sortOrder: 10,
  },
  {
    locale: 'zh-CN',
    languageCode: 'zh',
    regionCode: 'CN',
    nameNative: '中文',
    nameVi: 'Tiếng Trung',
    nameEn: 'Chinese (Simplified)',
    direction: 'LTR',
    dateFormat: 'yyyy/MM/dd',
    numberFormat: '1,234.56',
    sortOrder: 20,
  },
  {
    locale: 'ru-RU',
    languageCode: 'ru',
    regionCode: 'RU',
    nameNative: 'Русский',
    nameVi: 'Tiếng Nga',
    nameEn: 'Russian',
    direction: 'LTR',
    dateFormat: 'dd.MM.yyyy',
    numberFormat: '1 234,56',
    sortOrder: 30,
  },
  {
    locale: 'en-US',
    languageCode: 'en',
    regionCode: 'US',
    nameNative: 'English',
    nameVi: 'Tiếng Anh',
    nameEn: 'English',
    direction: 'LTR',
    dateFormat: 'MM/dd/yyyy',
    numberFormat: '1,234.56',
    sortOrder: 40,
  },

  // -------------------------------------------------------------------------
  // READY TO ENABLE — uncomment the entry, insert the matching
  // `supported_locales` row and the `customer_ui_translations` rows, done.
  // No other source file changes. (Kept here so the extensibility claim is
  // testable rather than aspirational.)
  // -------------------------------------------------------------------------
  // {
  //   locale: 'ko-KR',
  //   languageCode: 'ko',
  //   regionCode: 'KR',
  //   nameNative: '한국어',
  //   nameVi: 'Tiếng Hàn',
  //   nameEn: 'Korean',
  //   direction: 'LTR',
  //   dateFormat: 'yyyy.MM.dd',
  //   numberFormat: '1,234.56',
  //   sortOrder: 50,
  // },
  // {
  //   locale: 'ja-JP',
  //   languageCode: 'ja',
  //   regionCode: 'JP',
  //   nameNative: '日本語',
  //   nameVi: 'Tiếng Nhật',
  //   nameEn: 'Japanese',
  //   direction: 'LTR',
  //   dateFormat: 'yyyy/MM/dd',
  //   numberFormat: '1,234.56',
  //   sortOrder: 60,
  // },
  // {
  //   locale: 'th-TH',
  //   languageCode: 'th',
  //   regionCode: 'TH',
  //   nameNative: 'ไทย',
  //   nameVi: 'Tiếng Thái',
  //   nameEn: 'Thai',
  //   direction: 'LTR',
  //   dateFormat: 'dd/MM/yyyy',
  //   numberFormat: '1,234.56',
  //   sortOrder: 70,
  // },
  // {
  //   locale: 'fr-FR',
  //   languageCode: 'fr',
  //   regionCode: 'FR',
  //   nameNative: 'Français',
  //   nameVi: 'Tiếng Pháp',
  //   nameEn: 'French',
  //   direction: 'LTR',
  //   dateFormat: 'dd/MM/yyyy',
  //   numberFormat: '1 234,56',
  //   sortOrder: 80,
  // },
  // {
  //   locale: 'de-DE',
  //   languageCode: 'de',
  //   regionCode: 'DE',
  //   nameNative: 'Deutsch',
  //   nameVi: 'Tiếng Đức',
  //   nameEn: 'German',
  //   direction: 'LTR',
  //   dateFormat: 'dd.MM.yyyy',
  //   numberFormat: '1.234,56',
  //   sortOrder: 90,
  // },
] as const satisfies readonly LocaleInfo[];

/** Union of the tags currently in the catalogue. Widens automatically. */
export type KnownLocale = (typeof SUPPORTED_LOCALES)[number]['locale'];

/** The source language. Never configurable — the whole pipeline assumes it. */
export const DEFAULT_LOCALE = 'vi-VN';

/** Alias used by the translation pipeline; identical to `DEFAULT_LOCALE`. */
export const SOURCE_LOCALE = DEFAULT_LOCALE;

const BY_LOCALE: ReadonlyMap<string, LocaleInfo> = new Map(
  SUPPORTED_LOCALES.map((l) => [l.locale.toLowerCase(), l as LocaleInfo]),
);

/**
 * Canonicalise a tag's casing: `ZH-cn` → `zh-CN`, `EN` → `en`.
 * Never throws; returns `''` for empty/nullish input.
 */
export function canonicalizeLocale(tag: string | null | undefined): string {
  if (typeof tag !== 'string') return '';
  const trimmed = tag.trim();
  if (trimmed === '') return '';
  const parts = trimmed.replace(/_/g, '-').split('-');
  const lang = (parts[0] ?? '').toLowerCase();
  if (lang === '') return '';
  const rest = parts.slice(1).map((p) => (p.length === 2 ? p.toUpperCase() : p.toLowerCase()));
  return [lang, ...rest].join('-');
}

/** Type guard against the catalogue. Case-insensitive. */
export function isSupportedLocale(tag: string | null | undefined): tag is KnownLocale {
  if (typeof tag !== 'string') return false;
  return BY_LOCALE.has(canonicalizeLocale(tag).toLowerCase());
}

/** Catalogue entry or `undefined`. Case-insensitive. */
export function getLocaleInfo(tag: string | null | undefined): LocaleInfo | undefined {
  if (typeof tag !== 'string') return undefined;
  return BY_LOCALE.get(canonicalizeLocale(tag).toLowerCase());
}

/** Catalogue entry, falling back to the source locale. Never `undefined`. */
export function getLocaleInfoOrDefault(tag: string | null | undefined): LocaleInfo {
  return getLocaleInfo(tag) ?? (BY_LOCALE.get(DEFAULT_LOCALE.toLowerCase()) as LocaleInfo);
}

/** ISO 639-1 part of a tag: `zh-CN` → `zh`. `''` when unparseable. */
export function languageOf(tag: string | null | undefined): string {
  return canonicalizeLocale(tag).split('-')[0] ?? '';
}

/** Region part of a tag: `zh-CN` → `CN`. `null` for language-only tags. */
export function regionOf(tag: string | null | undefined): string | null {
  const parts = canonicalizeLocale(tag).split('-');
  const region = parts.find((p) => /^[A-Z]{2}$/.test(p));
  return region ?? null;
}

export function localeDirection(tag: string | null | undefined): TextDirection {
  return getLocaleInfoOrDefault(tag).direction;
}

/**
 * The catalogue entries for the tags a branch/tenant has enabled, in switcher
 * order. Unknown tags are dropped (they cannot be rendered safely anyway).
 */
export function listLocales(enabled?: readonly string[]): readonly LocaleInfo[] {
  const source =
    enabled === undefined
      ? (SUPPORTED_LOCALES as readonly LocaleInfo[])
      : enabled.map((t) => getLocaleInfo(t)).filter((l): l is LocaleInfo => l !== undefined);
  return [...source].sort((a, b) => a.sortOrder - b.sortOrder || a.locale.localeCompare(b.locale));
}
